/**
 * product-oos.js — PDP out-of-stock waitlist (back-in-stock email signup).
 *
 * Mirrors the headless `FormOOS` component
 * (templates/ProductTemplate/components/FormOOS/index.tsx in the headless repo)
 * — same endpoint, same payload shape, same success/error envelope:
 *
 *   GET https://app.backinstock.org/stock_notification/create.json
 *     ?shop=<myshopify-domain>
 *     &notification[email]=<email>
 *     &notification[product_no]=<numeric product id>
 *     &notification[quantity_required]=1
 *     &notification[accepts_marketing]=true
 *     &notification[customer_utc_offset]=<seconds-from-utc>
 *     &variant[variant_no]=<numeric variant id>
 *
 * Success envelope: `data.status !== 'Error'`, `data.message` (string).
 * Error envelope:   `data.status === 'Error'`, `data.errors` (string OR object
 *                   keyed by field — first value joined).
 *
 * The form's variant ID is kept in sync with the PDP size selector by
 * product-form.js (which dispatches the existing `change` listener on the
 * surrounding form). This script only owns submit handling + success/error
 * rendering.
 *
 * No framework. Vanilla JS, no dependencies, defer-loaded.
 */

(function () {
  'use strict';

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function getText(root, selector, fallback) {
    const el = root.querySelector(selector);
    return (el && el.textContent && el.textContent.trim()) || fallback;
  }

  function buildUrl(shop, productId, variantId, email) {
    const utcOffset = new Date().getTimezoneOffset() * -60;
    const params = new URLSearchParams({
      shop: shop,
      'notification[email]': email,
      'notification[product_no]': productId,
      'notification[quantity_required]': '1',
      'notification[accepts_marketing]': 'true',
      'notification[customer_utc_offset]': String(utcOffset),
      'variant[variant_no]': variantId,
    });
    return `https://app.backinstock.org/stock_notification/create.json?${params.toString()}`;
  }

  function extractErrorMessage(payload) {
    if (!payload) return null;
    if (typeof payload.errors === 'string') return payload.errors;
    if (payload.errors && typeof payload.errors === 'object') {
      for (const key in payload.errors) {
        const val = payload.errors[key];
        if (Array.isArray(val)) return val.join(', ');
        if (typeof val === 'string') return val;
      }
    }
    return null;
  }

  function validateEmail(email, copy) {
    if (!email) return copy.required;
    if (!EMAIL_REGEX.test(email)) return copy.invalid;
    return null;
  }

  function mount(root) {
    if (root.dataset.productOosMounted === 'true') return;
    root.dataset.productOosMounted = 'true';

    const form = root.querySelector('[data-product-oos-form-form]');
    const emailInput = root.querySelector('[data-product-oos-email]');
    const submitBtn = root.querySelector('[data-product-oos-submit]');
    const errorEl = root.querySelector('[data-product-oos-error]');
    const successEl = root.querySelector('[data-product-oos-success]');
    if (!form || !emailInput || !submitBtn) return;

    // Localised copy is sourced from server-rendered text content where possible;
    // a couple of validation messages live only on this side and fall through
    // to English defaults if the locale keys aren't present.
    const errorCopy = {
      required: 'Email is required',
      invalid: 'Please enter a valid email address',
      generic: 'An error occurred. Please try again.',
      registered: 'Your notification has been registered.',
    };

    function show(el, message) {
      el.textContent = message;
      el.removeAttribute('hidden');
    }
    function hide(el) {
      el.textContent = '';
      el.setAttribute('hidden', '');
    }

    function reset() {
      hide(errorEl);
      hide(successEl);
      form.removeAttribute('hidden');
    }

    form.addEventListener('submit', async function handler(event) {
      event.preventDefault();
      reset();

      const shop = root.dataset.shop;
      const productId = root.dataset.productId;
      const variantId = root.dataset.variantId;
      const email = (emailInput.value || '').trim();

      const validationError = validateEmail(email, errorCopy);
      if (validationError) {
        show(errorEl, validationError);
        return;
      }

      if (!shop || !productId || !variantId) {
        show(errorEl, errorCopy.generic);
        return;
      }

      submitBtn.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(
          buildUrl(shop, productId, variantId, email),
          { method: 'GET', headers: { 'Content-Type': 'application/json' } },
        );

        const payload = await response.json().catch(() => null);

        if (!response.ok || (payload && payload.status === 'Error')) {
          const msg = extractErrorMessage(payload) || errorCopy.generic;
          show(errorEl, msg);
          return;
        }

        const successMessage = (payload && payload.message) || errorCopy.registered;
        form.setAttribute('hidden', '');
        show(successEl, successMessage);
      } catch (err) {
        console.error('[product-oos] back-in-stock signup failed', err);
        show(errorEl, errorCopy.generic);
      } finally {
        submitBtn.removeAttribute('aria-busy');
      }
    });
  }

  function init() {
    document
      .querySelectorAll('[data-product-oos-form]')
      .forEach(mount);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
